import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik, FormikProvider } from 'formik';
import {
  AlertError,
  Button,
  Form,
  FormFieldset,
  FormikInputText,
  FormikDropdownSearch,
  FormikPhoneInput,
  isFormikValid,
  FormLabel,
  FieldError,
} from '@percihealth/react';
import {
  ReferralCreateLandgSchema,
  ReferralCreateLandg,
  allJoinReasons,
  ReferralType,
  ReferralDocumentType,
  signedFormConsent,
  ReferralStoredDocument,
  ReferralStoredDocuments,
  MAX_REFERRAL_DOCUMENTS,
  findReferralDocumentOfType,
  filterReferralDocumentsOfType,
} from '@packages/core-shared';
import { zodValidate } from '@packages/web-shared';
import {
  ReferralsApiRepository,
  useGenerateReferralId,
} from '@packages/web-shared/api';

import {
  DocumentUpload,
  ReferralInternalNotesField,
} from '@packages/web-shared/components/referrals';
import TrashIcon from '@packages/web-shared/components/icons/TrashIcon';

const joinReasonOptions = allJoinReasons.map((cr) => ({
  value: cr.id,
  name: cr.value,
}));

export default function ReferralForm() {
  const referralsApiRepository = useMemo(
    () => new ReferralsApiRepository(),
    [],
  );

  const navigate = useNavigate();
  const [responseError, setResponseError] = useState<string | undefined>(
    undefined,
  );

  const { referralId, errorMessage: referralIdError } = useGenerateReferralId();

  const referralHandleOnsubmit = async (values: any, actions: any) => {
    if (!isFormikValid(formik)) {
      return;
    }

    try {
      await referralsApiRepository.create({
        ...values,
        type: ReferralType.landg,
        // What this form knows about the patient's consent: their signed
        // form is attached to the referral, so the referral says so rather
        // than leaving the endpoint to assume it (see ReferralConsent.md).
        consent: signedFormConsent,
      });

      navigate('/referred');
    } catch (error) {
      console.error(error);
      setResponseError((error as Error).message);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: referralId,
      patient: {
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
      },
      joinReasonId: allJoinReasons[0].id,
      caseManager: {
        email: '',
        firstname: '',
        lastname: '',
      },
      gip: {
        code: '',
      },
      notes: {
        internal: '',
      },
      documents: [],
    } as ReferralCreateLandg,
    validate: zodValidate(ReferralCreateLandgSchema),
    onSubmit: referralHandleOnsubmit,
  });

  useEffect(() => {
    if (referralId) {
      void formik.setFieldValue('id', referralId, false);
    }
  }, [formik.setFieldValue, referralId]);

  // The documents are one typed array (PPL-3549): the sections below read
  // their own type out of it and write the whole array back
  const documents = formik.values.documents ?? [];
  // L&G's own field is "Latest clinical report": one slot, so re-uploading
  // replaces it. Other organizations can attach several (PPL-3549)
  const latestClinicalReport = findReferralDocumentOfType(
    documents,
    ReferralDocumentType.clinicalReport,
  );
  const consent = findReferralDocumentOfType(
    documents,
    ReferralDocumentType.patientReferralConsent,
  );
  const referralForms = filterReferralDocumentsOfType(
    documents,
    ReferralDocumentType.supporting,
  );

  const setDocuments = (updated: ReferralStoredDocuments) =>
    formik.setFieldValue('documents', updated, true /*validate*/);

  // A referral holds one consent and one clinical report, so uploading again
  // replaces the one it has
  const replaceDocument = (uploaded: ReferralStoredDocuments) =>
    setDocuments([
      ...documents.filter(
        (doc) => !uploaded.some((up) => up.type === doc.type),
      ),
      ...uploaded,
    ]);

  const addDocuments = (uploaded: ReferralStoredDocuments) =>
    setDocuments([...documents, ...uploaded]);

  const removeDocument = (document: ReferralStoredDocument) =>
    setDocuments(documents.filter((doc) => doc.path !== document.path));

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <FormFieldset title="Patient's details">
          <FormikInputText
            name="patient.firstname"
            required
            label="Patient first name"
            value={formik.values.patient.firstname}
          />
          <FormikInputText
            name="patient.lastname"
            required
            label="Patient surname"
            value={formik.values.patient.lastname}
          />
          <FormikInputText
            name="patient.email"
            required
            label="Patient email address"
            value={formik.values.patient.email}
            onBlur={(e) => {
              formik.setFieldValue(
                'patient.email',
                e.target.value?.toLowerCase(),
              );
              formik.setFieldTouched(
                'patient.email',
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
          <FormikPhoneInput
            name="patient.phone"
            label="Patient telephone number"
            value={formik.values.patient.phone}
          />
          <FormikDropdownSearch
            label="Join reason"
            name="joinReasonId"
            required
            multiple={false}
            value={formik.values.joinReasonId.toString()}
            options={joinReasonOptions}
          />
        </FormFieldset>
        <FormFieldset title="Case manager">
          <FormikInputText
            id="caseManager.firstname"
            name="caseManager.firstname"
            required
            label="Case manager first name"
            value={formik.values.caseManager.firstname}
          />
          <FormikInputText
            id="caseManager.lastname"
            name="caseManager.lastname"
            required
            label="Case manager surname"
            value={formik.values.caseManager.lastname}
          />
          <FormikInputText
            id="caseManager.email"
            name="caseManager.email"
            required
            label="Case manager email address"
            value={formik.values.caseManager.email}
            onBlur={(e) => {
              formik.setFieldValue(
                'caseManager.email',
                e.target.value?.toLowerCase(),
              );
              formik.setFieldTouched(
                'caseManager.email',
                true /* set touched=true */,
                false /* do not validate, validation is set onChange */,
              );
            }}
          />
        </FormFieldset>
        <FormFieldset title="GIP">
          <FormikInputText
            id="gip.code"
            name="gip.code"
            required
            prefix="GIP-"
            placeholder="123456"
            label="Group Income Protection (GIP) code"
            value={formik.values.gip.code}
          />
          {/* Notes sit under the GIP code on the form (PPL-3563), but they are
              the referral's own notes, not part of the GIP details */}
          <ReferralInternalNotesField />
        </FormFieldset>
        <FormFieldset title="Documents">
          <div>
            <FormLabel required>
              Latest clinical report (PDF, Word Document)
            </FormLabel>
            {latestClinicalReport && (
              <div
                style={{
                  marginTop: '8px',
                  marginBottom: '24px',
                  wordBreak: 'break-word',
                }}
              >
                {latestClinicalReport.name}
              </div>
            )}
            {!latestClinicalReport && formik.submitCount > 0 && (
              <FieldError style={{ position: 'initial' }}>
                Latest clinical report is required
              </FieldError>
            )}
            <DocumentUpload
              type={ReferralDocumentType.clinicalReport}
              extensions=".pdf,.doc,.docx"
              referralId={referralId}
              onUploaded={replaceDocument}
            />
          </div>

          <div>
            <FormLabel required>Consent form (PDF, Word Document)</FormLabel>
            {consent && (
              <div
                style={{
                  marginTop: '8px',
                  marginBottom: '24px',
                  wordBreak: 'break-word',
                }}
              >
                {consent.name}
              </div>
            )}
            {!consent && formik.submitCount > 0 && (
              <FieldError style={{ position: 'initial' }}>
                Consent form is required
              </FieldError>
            )}
            <DocumentUpload
              type={ReferralDocumentType.patientReferralConsent}
              extensions=".pdf,.doc,.docx"
              referralId={referralId}
              onUploaded={replaceDocument}
            />
          </div>

          <div>
            <FormLabel>Referral forms (PDF, Microsoft Word, Images)</FormLabel>
            {referralForms.length > 0 && (
              <div style={{ marginLeft: '8px', marginBottom: '24px' }}>
                {referralForms.map((form) => (
                  <div
                    style={{ marginTop: '8px', display: 'flex', gap: '16px' }}
                    key={form.path}
                  >
                    <div style={{ wordBreak: 'break-word' }}>{form.name}</div>
                    <div
                      style={{
                        cursor: 'pointer',
                        color: 'var(--global-color-error)',
                      }}
                      onClick={() => removeDocument(form)}
                    >
                      <TrashIcon />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <DocumentUpload
              type={ReferralDocumentType.supporting}
              extensions=".pdf, .doc, .docx, .jpg, .jpeg, .png"
              referralId={referralId}
              slotsLeft={MAX_REFERRAL_DOCUMENTS - documents.length}
              onUploaded={addDocuments}
            />
          </div>
        </FormFieldset>

        <FormFieldset title="">
          <div>
            <Button
              type="submit"
              level="cta"
              size="large"
              disabled={!referralId}
              submitting={formik.isSubmitting}
            >
              Submit referral
            </Button>
          </div>
          <div className="full-width">
            {(responseError || referralIdError) && (
              <AlertError>{responseError || referralIdError}</AlertError>
            )}
          </div>
        </FormFieldset>
      </Form>
    </FormikProvider>
  );
}
